 import { Carousel } from "antd";
import "antd/dist/reset.css";
import { useRef, useState, useEffect } from "react";
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
} from "@ant-design/icons";

const BASE_URL = "http://localhost:1337"; // ✅ change if needed

const TravelBanner = ({ title, images = [], shortDescription }) => {
  const carouselRef = useRef();
  const galleryRef = useRef();

  const [active, setActive] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    if (showGallery && galleryRef.current) {
      galleryRef.current.goTo(active, true);
    }
  }, [showGallery]);

  const handleThumbClick = (index) => {
    setActive(index);
    carouselRef.current?.goTo(index);
  };

  // ✅ Handle BOTH object & string
  const getUrl = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    return item.url || "";
  };

  const getMediaUrl = (item) => {
    const url = getUrl(item);
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE_URL}${url}`;
  };

  const isVideo = (item) => {
    const url = getUrl(item);
    return /\.(mp4|webm|ogg)$/i.test(url);
  };

  return (
    <>
      {/* 🔥 MAIN SECTION */}
      <div className="relative w-full grid grid-cols-1 lg:grid-cols-[55%_45%] gap-3">
        
        {/* LEFT SLIDER */}
        <div
          className="rounded-xl overflow-hidden relative"
          onClick={() => setShowGallery(true)}
        >
          <div className="relative">
            <Carousel
              ref={carouselRef}
              afterChange={(current) => setActive(current)}
              dots={false}
            >
              {images.map((img, i) => (
                <div key={i}>
                  <div className="h-[430px] w-full bg-black">
                    {isVideo(img) ? (
                      <video
                        src={getMediaUrl(img)}
                        className="w-full h-full object-cover"
                        controls
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={getMediaUrl(img)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    )}
                  </div>
                </div>
              ))}
            </Carousel>

            {/* ARROWS */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                carouselRef.current.prev();
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-black/40 text-white p-2 rounded-full"
            >
              <LeftOutlined />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                carouselRef.current.next();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-black/40 text-white p-2 rounded-full"
            >
              <RightOutlined />
            </button>
          </div>
        </div>

        {/* RIGHT GRID */}
        <div className="hidden lg:grid grid-cols-2 grid-rows-3 gap-2 h-[430px]">
          {images.slice(0, 6).map((img, i) => {
            const isLast = i === 5 && images.length > 6;

            return (
              <div
                key={i}
                onClick={() =>
                  isLast ? setShowGallery(true) : handleThumbClick(i)
                }
                className={`relative cursor-pointer rounded-lg overflow-hidden border-2 ${
                  active === i ? "border-blue-500" : "border-transparent"
                }`}
              >
                {isVideo(img) ? (
                  <video
                    src={getMediaUrl(img)}
                    className="w-full h-full object-cover"
                    muted
                  />
                ) : (
                  <img
                    src={getMediaUrl(img)}
                    className="w-full h-full object-cover"
                    alt=""
                    onError={(e) => (e.target.style.display = "none")}
                  />
                )}

                {/* ▶ PLAY ICON */}
                {isVideo(img) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-xl">
                    ▶
                  </div>
                )}

                {/* VIEW ALL */}
                {isLast && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm font-semibold">
                    +{images.length - 5} View All
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 🔥 FULLSCREEN */}
      {showGallery && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col">
          
          {/* CLOSE */}
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-5 right-5 text-white text-2xl z-10"
          >
            <CloseOutlined />
          </button>

          {/* MAIN */}
          <div className="flex-1 relative">
            <Carousel
              ref={galleryRef}
              afterChange={(i) => setActive(i)}
              dots={false}
              infinite={false}
            >
              {images.map((img, i) => (
                <div key={i} className="h-full">
                  <div className="w-full h-full flex items-center justify-center">
                    {isVideo(img) ? (
                      <video
                        src={getMediaUrl(img)}
                        controls
                        className="max-h-[80vh] max-w-[90vw]"
                      />
                    ) : (
                      <img
                        src={getMediaUrl(img)}
                        className="max-h-[80vh] max-w-[90vw] object-contain"
                        alt=""
                      />
                    )}
                  </div>
                </div>
              ))}
            </Carousel>

            {/* ARROWS */}
            <button
              onClick={() => galleryRef.current.prev()}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-white text-3xl"
            >
              ‹
            </button>

            <button
              onClick={() => galleryRef.current.next()}
              className="absolute right-5 top-1/2 -translate-y-1/2 text-white text-3xl"
            >
              ›
            </button>
          </div>

          {/* BOTTOM */}
          <div className="h-[100px] bg-black/80 flex gap-3 px-4 overflow-x-auto items-center">
            {images.map((img, i) => (
              <img
                key={i}
                src={getMediaUrl(img)}
                onClick={() => galleryRef.current?.goTo(i, true)}
                className={`h-16 w-24 object-cover cursor-pointer rounded border-2 ${
                  active === i ? "border-white" : "border-transparent"
                }`}
                alt=""
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default TravelBanner;